import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import BrandMeta from "@/components/BrandMeta";
import { ApplyProvider } from "@/hooks/use-apply-context";
import ApplyAlreadySubmittedGate from "@/components/ApplyAlreadySubmittedGate";

// Lazy-load UI shell components not needed for first paint
const LazyTooltipProvider = lazy(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));
const LazyToaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const LazySonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Index = lazy(() => import("./pages/Index"));
const RevokeConsumerReport = lazy(() => import("./pages/RevokeConsumerReport"));

// Lazy-loaded pages for code splitting
const ShortUrlRedirect = lazy(() => import("./pages/ShortUrlRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StudentLoanRefinance = lazy(() => import("./pages/StudentLoanRefinance"));
const StudentLoanAssets = lazy(() => import("./pages/StudentLoanAssets"));
const StudentLoanGraduation = lazy(() => import("./pages/StudentLoanGraduation"));
const StudentLoanEmployment = lazy(() => import("./pages/StudentLoanEmployment"));
const StudentLoanCreditScore = lazy(() => import("./pages/StudentLoanCreditScore"));
const StudentLoanIncome = lazy(() => import("./pages/StudentLoanIncome"));
const StudentLoanEmail = lazy(() => import("./pages/StudentLoanEmail"));
const ApplyLoanPurpose = lazy(() => import("./pages/ApplyLoanPurpose"));
const ApplyAmount = lazy(() => import("./pages/ApplyAmount"));
const ApplyCreditScore = lazy(() => import("./pages/ApplyCreditScore"));
const ApplyEmployment = lazy(() => import("./pages/ApplyEmployment"));
const ApplyIncome = lazy(() => import("./pages/ApplyIncome"));
const ApplyHousing = lazy(() => import("./pages/ApplyHousing"));
const ApplyTimeAtResidence = lazy(() => import("./pages/ApplyTimeAtResidence"));
const ApplyEducation = lazy(() => import("./pages/ApplyEducation"));
const ApplyPaymentFrequency = lazy(() => import("./pages/ApplyPaymentFrequency"));
const ApplyPersonalInfo = lazy(() => import("./pages/ApplyPersonalInfo"));
const ApplyContact = lazy(() => import("./pages/ApplyContact"));
const ApplySSN = lazy(() => import("./pages/ApplySSN"));
const ApplySummary = lazy(() => import("./pages/ApplySummary"));
const ApplyOffers = lazy(() => import("./pages/ApplyOffers"));
const ApplyOffersResult = lazy(() => import("./pages/ApplyOffersResult"));
const ApplyOffersResultSample = lazy(() => import("./pages/ApplyOffersResultSample"));
const ApplyOffersResultNoOffersSample = lazy(() => import("./pages/ApplyOffersResultNoOffersSample"));
const ExploreLoans = lazy(() => import("./pages/ExploreLoans"));
const AutoRefinanceBalance = lazy(() => import("./pages/AutoRefinanceBalance"));
const AutoRefinanceVehicle = lazy(() => import("./pages/AutoRefinanceVehicle"));
const AutoRefinanceCreditScore = lazy(() => import("./pages/AutoRefinanceCreditScore"));
const AutoRefinanceEmail = lazy(() => import("./pages/AutoRefinanceEmail"));
const MortgageRefinanceZip = lazy(() => import("./pages/MortgageRefinanceZip"));
const MortgageRefinancePropertyValue = lazy(() => import("./pages/MortgageRefinancePropertyValue"));
const MortgageRefinanceBalance = lazy(() => import("./pages/MortgageRefinanceBalance"));
const MortgageRefinanceCreditScore = lazy(() => import("./pages/MortgageRefinanceCreditScore"));
const MortgageRefinanceCashOut = lazy(() => import("./pages/MortgageRefinanceCashOut"));
const MortgageRefinanceEmail = lazy(() => import("./pages/MortgageRefinanceEmail"));
const CreditCardsExplore = lazy(() => import("./pages/CreditCardsExplore"));
const CreditCardsPreferences = lazy(() => import("./pages/CreditCardsPreferences"));
const CreditCardsCreditScore = lazy(() => import("./pages/CreditCardsCreditScore"));
const CreditCardsIncome = lazy(() => import("./pages/CreditCardsIncome"));
const CreditCardsEmail = lazy(() => import("./pages/CreditCardsEmail"));
const SavingsExplore = lazy(() => import("./pages/SavingsExplore"));
const SavingsDepositAmount = lazy(() => import("./pages/SavingsDepositAmount"));
const SavingsGoals = lazy(() => import("./pages/SavingsGoals"));
const SavingsIncome = lazy(() => import("./pages/SavingsIncome"));
const SavingsEmail = lazy(() => import("./pages/SavingsEmail"));
const LoanCalculator = lazy(() => import("./pages/LoanCalculator"));
const CreditScoreGuide = lazy(() => import("./pages/CreditScoreGuide"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const WhyUsPage = lazy(() => import("./pages/WhyUsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CCPANotice = lazy(() => import("./pages/CCPANotice"));
const ElectronicConsent = lazy(() => import("./pages/ElectronicConsent"));
const CreditAuthorization = lazy(() => import("./pages/CreditAuthorization"));
const AdvertisingDisclosure = lazy(() => import("./pages/AdvertisingDisclosure"));
const FinancialPrivacyNotice = lazy(() => import("./pages/FinancialPrivacyNotice"));

const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Partners = lazy(() => import("./pages/Partners"));
const EmailPreview = lazy(() => import("./pages/EmailPreview"));
const EmailPreviewOfferResults = lazy(() => import("./pages/EmailPreviewOfferResults"));
const EmailPreviewNoOffers = lazy(() => import("./pages/EmailPreviewNoOffers"));
const SettingsLogin = lazy(() => import("./pages/SettingsLogin"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const Home = lazy(() => import("./pages/Home"));
const HomeUsers = lazy(() => import("./pages/HomeUsers"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportApplications = lazy(() => import("./pages/ReportApplications"));
const ReportApplicationDetail = lazy(() => import("./pages/ReportApplicationDetail"));
const ReportApiLogs = lazy(() => import("./pages/ReportApiLogs"));
const ReportPending = lazy(() => import("./pages/ReportPending"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const ApplyRoutes = () => {
  const Gate = ApplyAlreadySubmittedGate;
  return (
    <ApplyProvider>
      <Routes>
        <Route index element={<Gate><ApplyLoanPurpose /></Gate>} />
        <Route path="amount" element={<Gate><ApplyAmount /></Gate>} />
        <Route path="credit-score" element={<Gate><ApplyCreditScore /></Gate>} />
        <Route path="payment-frequency" element={<Gate><ApplyPaymentFrequency /></Gate>} />
        <Route path="employment" element={<Gate><ApplyEmployment /></Gate>} />
        <Route path="income" element={<Gate><ApplyIncome /></Gate>} />
        <Route path="housing" element={<Gate><ApplyHousing /></Gate>} />
        <Route path="time-at-residence" element={<Gate><ApplyTimeAtResidence /></Gate>} />
        <Route path="education" element={<Gate><ApplyEducation /></Gate>} />
        <Route path="personal-info" element={<Gate><ApplyPersonalInfo /></Gate>} />
        <Route path="contact" element={<Gate><ApplyContact /></Gate>} />
        <Route path="ssn" element={<Gate><ApplySSN /></Gate>} />
        <Route path="summary" element={<Gate><ApplySummary /></Gate>} />
        <Route path="offers" element={<Gate><ApplyOffers /></Gate>} />
        <Route path="offers-result/sample" element={<ApplyOffersResultSample />} />
        <Route path="offers-result/no-offers-sample" element={<ApplyOffersResultNoOffersSample />} />
        <Route path="offers-result/:applicationId" element={<ApplyOffersResult />} />
      </Routes>
    </ApplyProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <LazyTooltipProvider>
        <LazyToaster />
        <LazySonner />
        <BrowserRouter>
          <ScrollToTop />
          <BrandMeta />
          <Suspense fallback={null}>
            <Routes>
              {/* Short URL redirect */}
              <Route path="/r/:code" element={<ShortUrlRedirect />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/" element={<Index />} />
              {/* Loan Calculator */}
              <Route path="/loan-calculator" element={<LoanCalculator />} />
              {/* Credit Score Guide */}
              <Route path="/credit-score-guide" element={<CreditScoreGuide />} />
              {/* FAQ Page */}
              <Route path="/faq" element={<FAQPage />} />
              {/* How It Works Page */}
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              {/* Why Us Page */}
              <Route path="/why-us" element={<WhyUsPage />} />
              {/* Privacy Policy Page */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* Terms of Service Page */}
              <Route path="/terms-of-service" element={<TermsOfService />} />
              {/* CCPA Notice Page */}
              <Route path="/ccpa-notice" element={<CCPANotice />} />
              {/* Electronic Consent Page */}
              <Route path="/electronic-consent" element={<ElectronicConsent />} />
              {/* Credit Authorization Page */}
              <Route path="/credit-authorization" element={<CreditAuthorization />} />
              {/* Revoke Consumer Report Access Page */}
              <Route path="/revoke-consumer-report" element={<RevokeConsumerReport />} />
              {/* Advertising Disclosure Page */}
              <Route path="/advertising-disclosure" element={<AdvertisingDisclosure />} />
              <Route path="/financial-privacy-notice" element={<FinancialPrivacyNotice />} />
              
              {/* API Documentation */}
              <Route path="/api-docs" element={<ApiDocs />} />
              {/* Partners Page */}
              <Route path="/partners" element={<Partners />} />
              {/* Email Preview */}
              <Route path="/email-preview" element={<EmailPreview />} />
              <Route path="/email-preview/offer-results" element={<EmailPreviewOfferResults />} />
              <Route path="/email-preview/no-offers" element={<EmailPreviewNoOffers />} />
              {/* Explore Loans Page */}
              <Route path="/explore/loans" element={<ExploreLoans />} />
              {/* Personal Loan Application Funnel */}
              <Route path="/apply/*" element={<ApplyRoutes />} />
              <Route path="/:urlCode/apply/*" element={<ApplyRoutes />} />
              <Route path="/:urlCode/:customId/apply/*" element={<ApplyRoutes />} />
              {/* Student Loan Refinance Funnel */}
              <Route path="/student-loan-refinance" element={<StudentLoanRefinance />} />
              <Route path="/student-loan-refinance/assets" element={<StudentLoanAssets />} />
              <Route path="/student-loan-refinance/graduation" element={<StudentLoanGraduation />} />
              <Route path="/student-loan-refinance/employment" element={<StudentLoanEmployment />} />
              <Route path="/student-loan-refinance/credit-score" element={<StudentLoanCreditScore />} />
              <Route path="/student-loan-refinance/income" element={<StudentLoanIncome />} />
              <Route path="/student-loan-refinance/email" element={<StudentLoanEmail />} />
              {/* Auto Refinance Funnel */}
              <Route path="/auto-refinance" element={<AutoRefinanceBalance />} />
              <Route path="/auto-refinance/vehicle" element={<AutoRefinanceVehicle />} />
              <Route path="/auto-refinance/credit-score" element={<AutoRefinanceCreditScore />} />
              <Route path="/auto-refinance/email" element={<AutoRefinanceEmail />} />
              {/* Mortgage Refinance Funnel */}
              <Route path="/mortgage-refinance" element={<MortgageRefinanceZip />} />
              <Route path="/mortgage-refinance/property-value" element={<MortgageRefinancePropertyValue />} />
              <Route path="/mortgage-refinance/mortgage-balance" element={<MortgageRefinanceBalance />} />
              <Route path="/mortgage-refinance/credit-score" element={<MortgageRefinanceCreditScore />} />
              <Route path="/mortgage-refinance/cash-out" element={<MortgageRefinanceCashOut />} />
              <Route path="/mortgage-refinance/email" element={<MortgageRefinanceEmail />} />
              {/* Credit Cards Funnel */}
              <Route path="/credit-cards" element={<CreditCardsExplore />} />
              <Route path="/credit-cards/funnel" element={<CreditCardsPreferences />} />
              <Route path="/credit-cards/credit-score" element={<CreditCardsCreditScore />} />
              <Route path="/credit-cards/income" element={<CreditCardsIncome />} />
              <Route path="/credit-cards/email" element={<CreditCardsEmail />} />
              {/* Savings Page */}
              <Route path="/savings" element={<SavingsExplore />} />
              {/* Savings Funnel */}
              <Route path="/savings/funnel" element={<SavingsDepositAmount />} />
              <Route path="/savings/funnel/goals" element={<SavingsGoals />} />
              <Route path="/savings/funnel/income" element={<SavingsIncome />} />
              <Route path="/savings/funnel/email" element={<SavingsEmail />} />
              {/* Settings */}
              <Route path="/login" element={<SettingsLogin />} />
              <Route path="/home" element={<Home />} />
              <Route path="/home/users" element={<HomeUsers />} />
              <Route path="/home/settings" element={<SettingsPage />} />
              <Route path="/home/reports" element={<Reports />} />
              <Route path="/home/reports/applications" element={<ReportApplications />} />
              <Route path="/home/reports/applications/:applicationId" element={<ReportApplicationDetail />} />
              <Route path="/home/reports/api-logs" element={<ReportApiLogs />} />
              <Route path="/home/reports/pending" element={<ReportPending />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </LazyTooltipProvider>
    </Suspense>
  </QueryClientProvider>
);

export default App;